import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Syringe, ExternalLink, Info, Check } from 'lucide-react';
import { Link } from 'react-router-dom';

const RejseVaccinationer = () => {
  return (
    <div className="bg-white min-h-screen">
      <Helmet>
        <title>Rejsevaccination & Rådgivning | Hellerup Lægehus</title>
        <meta name="description" content="Skal du ud at rejse? Få information om rejsevaccinationer og rådgivning hos Hellerup Lægehus. Se anbefalinger og priser på vacciner her." />
        <meta property="og:title" content="Rejsevaccination & Rådgivning | Hellerup Lægehus" />
        <meta property="og:description" content="Skal du ud at rejse? Få information om rejsevaccinationer og rådgivning hos Hellerup Lægehus. Se anbefalinger og priser på vacciner her." />
        <html lang="da-DK" />
      </Helmet>

      {/* Hero Section */}
      <section className="bg-slate-50 pt-20 pb-12 md:pt-24 md:pb-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-4 text-[#4b717a]">
            <Syringe size={24} />
            <span className="text-sm font-bold tracking-widest uppercase">Patient Info</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-sans font-medium text-black mb-6 leading-tight tracking-tight">
            Rejse vaccinationer
          </h1>
          <p className="text-lg text-slate-600 max-w-3xl leading-relaxed">
            Ved udlandsrejser er der risiko for, at man bliver smittet med sygdomme, som ikke forekommer i Danmark. Flere af disse sygdomme kan forebygges ved vaccination.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
            <div className="lg:col-span-8">
              <div className="prose prose-slate max-w-none">
                <p className="text-slate-600 mb-8 leading-relaxed">
                  Det er forskelligt fra land til land, hvilke vaccinationer, der anbefales. Anbefalingerne afhænger af, hvor lang tid du skal være væk, og om du fx skal på storbyferie, chaterferie eller trekking. I visse lande er det påkrævet, at man er vaccineret mod gul feber.
                </p>

                <h2 className="text-2xl font-sans font-medium text-black mb-6 tracking-tight">
                  Nyttige links og anbefalinger
                </h2>
                <p className="text-slate-600 mb-6">
                  På disse links kan du se, hvilke vaccinationer, der anbefales til hvilke lande:
                </p>
                
                <ul className="space-y-4 mb-12">
                  <li>
                    <a 
                      href="https://rejse.ssi.dk/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-[#4b717a] hover:text-black transition-colors font-medium"
                    >
                      <ExternalLink size={18} />
                      rejse.ssi.dk
                    </a>
                  </li>
                  <li>
                    <a 
                      href="https://rejse.ssi.dk/generelle-rejseraad" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-[#4b717a] hover:text-black transition-colors font-medium"
                    >
                      <ExternalLink size={18} />
                      Generelle rejseråd (SSI)
                    </a>
                  </li>
                </ul>

                <div className="bg-[#4b717a] text-white p-8 mb-12">
                  <div className="flex items-start gap-4">
                    <Info className="mt-1 flex-shrink-0" size={24} />
                    <div>
                      <p className="text-lg leading-relaxed mb-4">
                        Hellerup Lægehus anbefaler, at du som patient kontakter en privat rejsevaccinationsklinik, der kan give dig uddybende rejserådgivning og de anbefalede vaccinationer.
                      </p>
                      <p className="text-white/90 italic">
                        Vi kan tilbyde at vaccinere mod udvalgte sygdomme – se venligst under "Priser" for liste over hvilke vacciner vi tilbyder.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-4">
                  <Link 
                    to="/priser" 
                    className="inline-flex items-center justify-center px-8 py-4 bg-black text-white hover:bg-slate-800 transition-all font-medium"
                  >
                    Se priser på vacciner
                  </Link>
                </div>
              </div>
            </div>

            <div className="lg:col-span-4">
              <div className="bg-slate-50 p-8 sticky top-24">
                <h3 className="text-xl font-sans font-medium text-black mb-6 tracking-tight">
                  Husk i god tid
                </h3>
                <ul className="space-y-4">
                  {[
                    'Tjek anbefalinger 4-6 uger før afrejse',
                    'Nogle vacciner kræver flere doser',
                    'Husk dit vaccinationskort',
                    'Overvej rejseforsikring'
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3 text-slate-600 text-sm">
                      <Check size={16} className="text-black mt-0.5 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RejseVaccinationer;
